import BackButton from '../misc/BackButton'
import BracketV2PhaseOneView from './BracketV2PhaseOneView'
import KnockoutMatchupV2 from '../BracketForm/KnockoutMatchupV2'
import { faPenToSquare } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { WC_NAME } from '../utils/Constants'
import { removeWhiteSpace } from '../utils/utils'
import {
  FIELD_DEPENDENCIES,
  FINAL_FIELDS,
  QUARTER_FIELDS,
  QUARTER_LAYOUT,
  ROUND_OF_16_FIELDS,
  ROUND_OF_32_FIELDS,
  ROUND_ONE_LAYOUT,
  ROUND_ONE_MATCHUP_MAP,
  ROUND_TWO_LAYOUT,
  ROUND_TWO_MATCH_FIELDS,
  SEMI_FIELDS,
  SEMI_LAYOUT,
  SEMI_MATCH_FIELDS,
  THIRD_FIELD,
  WINNER_FIELD,
  QUARTER_MATCH_FIELDS,
} from '../BracketForm/bracketStructureV2'
import { Button, Tooltip } from 'antd'
import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

const buildTeamOption = (teamId, rankings, sourceField = null) => {
  const ranking = rankings[String(teamId)]
  return ranking
    ? { ...ranking, value: teamId, sourceField }
    : { value: teamId, name: String(teamId), rank: null, flag: '/flags/Mystery.png', sourceField }
}

const formatRoundDate = (date) => String(date || '').replace(/,\s*20\d{2}$/, '')

const FIELD_POINTS = {
  ...Object.fromEntries(Array.from({ length: 32 }, (_, idx) => [`pick_${idx + 1}`, { main: 1, secondChance: 0 }])),
  ...Object.fromEntries(Array.from({ length: 16 }, (_, idx) => [`pick_r2_${idx + 1}`, { main: 2, secondChance: 2 }])),
  ...Object.fromEntries(Array.from({ length: 8 }, (_, idx) => [`pick_q${idx + 1}`, { main: 4, secondChance: 4 }])),
  ...Object.fromEntries(Array.from({ length: 4 }, (_, idx) => [`pick_s${idx + 1}`, { main: 8, secondChance: 8 }])),
  pick_f1: { main: 16, secondChance: 16 },
  pick_f2: { main: 16, secondChance: 16 },
  pick_third: { main: 24, secondChance: 24 },
  pick_winner: { main: 32, secondChance: 32 },
}

const renderConfiguredRound = ({ bracket, configs, getReadonlyOutcome, rankings }) => {
  return configs.map(({ field, label, options }) => (
    <KnockoutMatchupV2
      key={field}
      bracket={bracket}
      field={field}
      label={label}
      getReadonlyOutcome={getReadonlyOutcome}
      onChange={() => null}
      showSelection={false}
      options={options.map(option => option.value ? option : (bracket[option.field] ? buildTeamOption(bracket[option.field], rankings, option.field) : null))}
      readonly
    />
  ))
}

const renderSinglePick = ({ field, getReadonlyOutcome, label, picks, rankings }) => {
  const teamId = picks[field]
  const option = teamId ? buildTeamOption(teamId, rankings, field) : null
  const readonlyOutcome = teamId ? getReadonlyOutcome(field, String(teamId)) : null
  const readonlyClass = readonlyOutcome?.state ? `team-pick-button-${readonlyOutcome.state}` : ''

  return (
    <div className={`matchup-v2 bracket-v2-single-pick ${field === 'pick_third' ? 'bracket-v2-single-pick-third' : ''}`} key={`${field}-single-pick`}>
      <div className="matchup-v2-label wc-quaternary">{label}</div>
      <div className="team team-form team-top">
        <button className={`team-pick-button team-pick-button-single selected ${readonlyClass}`.trim()} disabled type="button">
          {option
            ? <span className="team-pick-main">
                <img className="inline-flag" src={option.flag} alt="flag" />
                <span className="wc-secondary">{option.value}</span>
              </span>
            : <span className="team-pick-placeholder" aria-hidden="true">&nbsp;</span>
          }
          {readonlyOutcome?.display ? <span className="team-pick-meta">
            <span className={`team-pick-status team-pick-status-${readonlyOutcome.state}`}>
              {readonlyOutcome.display}
            </span>
          </span> : null}
        </button>
      </div>
    </div>
  )
}

export default function BracketV2({ bracket, rankings, canEdit, showBackButton, backButtonOnClick, backButtonText }) {
  const mobileScrollerRef = useRef(null)
  const [mobileFocusIndex, setMobileFocusIndex] = useState(0)
  const [viewMode, setViewMode] = useState('knockout')
  const navigate = useNavigate()
  const picks = bracket.picks || {}
  const scorecard = bracket.scorecard
  const scorecardPicks = scorecard?.picks || {}
  const eliminated = new Set((scorecard?.eliminated || []).map(value => String(value)))
  const scorecardRoundOf32Slots = ROUND_OF_32_FIELDS.reduce((slots, field) => {
    const team = scorecardPicks[field]
    if (team) slots[String(team)] = field
    return slots
  }, {})
  const reachabilityCache = new Map()
  const winnerFlag = rankings[String(picks.pick_winner)]?.flag ?? '/flags/Mystery.png'
  const hasPhaseOneData = Boolean(Object.keys(picks._group_rankings || {}).length)
  const pointsFor = (field) => {
    const points = FIELD_POINTS[field]
    if (!points) return 0
    return bracket.second_chance ? points.secondChance : points.main
  }
  const actualPick = (field) => scorecardPicks[field] ? String(scorecardPicks[field]) : null
  const actualRoundOf32Slot = (team) => scorecardRoundOf32Slots[String(team)]
  const actualOrBracketPick = (field) => actualPick(field) || (picks[field] ? String(picks[field]) : null)
  const advancedToFinal = (team) => FINAL_FIELDS.some((field) => actualPick(field) === String(team))
  const reachedSemifinal = (team) => SEMI_FIELDS.some((field) => actualPick(field) === String(team))
  const semifinalParticipantsFor = (finalField) => (FIELD_DEPENDENCIES[finalField] || []).map((field) => actualPick(field)).filter(Boolean)
  const semifinalSideFor = (team) => {
    const teamId = String(team)

    if (actualPick('pick_f1') && semifinalParticipantsFor('pick_f1').includes(teamId)) return 'pick_f1'
    if (actualPick('pick_f2') && semifinalParticipantsFor('pick_f2').includes(teamId)) return 'pick_f2'

    return null
  }
  const lostSemifinal = (team) => Boolean(semifinalSideFor(team)) && !advancedToFinal(team)
  const canReachRoundOf32SlotInTournament = (team, field) => {
    if (eliminated.has(String(team))) return false

    const slot = actualRoundOf32Slot(team)
    if (slot) return slot === field

    return String(team) === actualOrBracketPick(field)
  }
  const canReachFieldInTournament = (team, field) => {
    const teamId = String(team)
    const cacheKey = `tournament:${teamId}:${field}`
    if (reachabilityCache.has(cacheKey)) return reachabilityCache.get(cacheKey)

    let result

    if (!teamId || eliminated.has(teamId)) {
      result = false
    } else if (actualPick(field)) {
      result = actualPick(field) === teamId
    } else if (ROUND_OF_32_FIELDS.includes(field)) {
      result = canReachRoundOf32SlotInTournament(teamId, field)
    } else {
      result = (FIELD_DEPENDENCIES[field] || []).some((dependency) => canReachFieldInTournament(teamId, dependency))
    }

    reachabilityCache.set(cacheKey, result)
    return result
  }
  const reachedSameRound = (team, roundFields) => roundFields.some((roundField) => actualPick(roundField) === String(team))
  const canReachSameRound = (team, field) => {
    if (ROUND_OF_32_FIELDS.includes(field)) {
      return reachedSameRound(team, ROUND_OF_32_FIELDS) || ROUND_OF_32_FIELDS.some((roundField) => canReachFieldInTournament(team, roundField))
    }
    if (ROUND_OF_16_FIELDS.includes(field)) {
      return reachedSameRound(team, ROUND_OF_16_FIELDS) || ROUND_OF_16_FIELDS.some((roundField) => canReachFieldInTournament(team, roundField))
    }
    if (QUARTER_FIELDS.includes(field)) {
      return reachedSameRound(team, QUARTER_FIELDS) || QUARTER_FIELDS.some((roundField) => canReachFieldInTournament(team, roundField))
    }
    if (SEMI_FIELDS.includes(field)) {
      return reachedSameRound(team, SEMI_FIELDS) || SEMI_FIELDS.some((roundField) => canReachFieldInTournament(team, roundField))
    }
    if (FINAL_FIELDS.includes(field)) {
      return reachedSameRound(team, FINAL_FIELDS) || FINAL_FIELDS.some((roundField) => canReachFieldInTournament(team, roundField))
    }
    if (field === THIRD_FIELD) return canStillFinishThird(team)
    if (field === WINNER_FIELD) return false
    return false
  }
  const canStillFinishThird = (team) => {
    const teamId = String(team)
    if (eliminated.has(teamId) || advancedToFinal(teamId)) return false
    if (reachedSemifinal(teamId)) return true

    return SEMI_FIELDS.some((field) => canReachFieldInTournament(teamId, field))
  }
  const canStillWinTournament = (team) => {
    const teamId = String(team)
    if (eliminated.has(teamId) || lostSemifinal(teamId)) return false
    if (advancedToFinal(teamId)) return true

    return FINAL_FIELDS.some((field) => canReachFieldInTournament(teamId, field))
  }
  const getThirdPlaceParticipantOutcome = (team, option) => {
    const teamId = String(team)
    const finalField = option?.thirdPlaceFinalField
    const semifinalFields = finalField ? (FIELD_DEPENDENCIES[finalField] || []) : []
    const semifinalWinner = finalField ? actualPick(finalField) : null
    const semifinalParticipants = semifinalFields.map((field) => actualPick(field)).filter(Boolean)

    if (semifinalWinner && semifinalWinner === teamId) {
      return { state: 'incorrect', display: null }
    }

    if (
      semifinalWinner &&
      semifinalParticipants.length === 2 &&
      semifinalParticipants.includes(teamId) &&
      semifinalWinner !== teamId
    ) {
      return { state: 'correct', display: null }
    }

    if (eliminated.has(teamId)) {
      return { state: 'eliminated', display: null }
    }

    return null
  }
  const getReadonlyOutcome = (field, optionValue, option = null) => {
    if (field === THIRD_FIELD && option?.thirdPlaceParticipant) {
      return getThirdPlaceParticipantOutcome(optionValue, option)
    }

    if (!optionValue || String(optionValue) !== String(picks[field])) return null

    if (scorecardPicks[field] && String(scorecardPicks[field]) === String(optionValue)) {
      const points = pointsFor(field)
      return { state: 'correct', display: points > 0 ? `+${points}` : null }
    }

    if (scorecardPicks[field] && String(scorecardPicks[field]) !== String(optionValue)) {
      return { state: 'incorrect', display: String(scorecardPicks[field]) }
    }

    if (field === THIRD_FIELD || field === WINNER_FIELD) {
      if (field === THIRD_FIELD) {
        if (advancedToFinal(optionValue)) return { state: 'incorrect', display: null }
        if (canStillFinishThird(optionValue)) return null
      } else {
        if (canStillWinTournament(optionValue)) return null
        if (lostSemifinal(optionValue)) return { state: 'eliminated', display: null }
      }
    } else if (canReachFieldInTournament(optionValue, field)) {
      return null
    }

    if (canReachSameRound(optionValue, field)) {
      return { state: 'misplaced', display: null }
    }

    if (eliminated.has(String(optionValue))) {
      return { state: 'eliminated', display: null }
    }

    return { state: 'incorrect', display: null }
  }

  const thirdPlaceOptions = () => {
    const leftLoser = picks.pick_f1 ? [picks.pick_s1, picks.pick_s2].find(teamId => teamId && teamId !== picks.pick_f1) : null
    const rightLoser = picks.pick_f2 ? [picks.pick_s3, picks.pick_s4].find(teamId => teamId && teamId !== picks.pick_f2) : null
    return [
      leftLoser ? { ...buildTeamOption(leftLoser, rankings), thirdPlaceParticipant: true, thirdPlaceFinalField: 'pick_f1' } : null,
      rightLoser ? { ...buildTeamOption(rightLoser, rankings), thirdPlaceParticipant: true, thirdPlaceFinalField: 'pick_f2' } : null,
    ]
  }

  const buildRoundOneConfigs = (matchNumbers) => matchNumbers.map(matchNumber => {
    const pickStart = ROUND_ONE_MATCHUP_MAP[matchNumber]
    const firstField = `pick_${pickStart}`
    const secondField = `pick_${pickStart + 1}`

    return {
      field: `pick_r2_${Math.ceil(pickStart / 2)}`,
      label: `Match ${matchNumber}`,
      options: [
        picks[firstField] ? buildTeamOption(picks[firstField], rankings, firstField) : null,
        picks[secondField] ? buildTeamOption(picks[secondField], rankings, secondField) : null,
      ]
    }
  })

  const buildConfiguredMatches = (matchNumbers, matchFieldMap) => matchNumbers.map(matchNumber => ({
    field: matchFieldMap[matchNumber].field,
    label: `Match ${matchNumber}`,
    options: matchFieldMap[matchNumber].options.map(field => ({ field }))
  }))

  const leftRoundOneConfigs = buildRoundOneConfigs(ROUND_ONE_LAYOUT.left)
  const rightRoundOneConfigs = buildRoundOneConfigs(ROUND_ONE_LAYOUT.right)
  const leftRoundTwoConfigs = buildConfiguredMatches(ROUND_TWO_LAYOUT.left, ROUND_TWO_MATCH_FIELDS)
  const rightRoundTwoConfigs = buildConfiguredMatches(ROUND_TWO_LAYOUT.right, ROUND_TWO_MATCH_FIELDS)
  const leftQuarterConfigs = buildConfiguredMatches(QUARTER_LAYOUT.left, QUARTER_MATCH_FIELDS)
  const rightQuarterConfigs = buildConfiguredMatches(QUARTER_LAYOUT.right, QUARTER_MATCH_FIELDS)
  const leftSemiConfigs = buildConfiguredMatches(SEMI_LAYOUT.left, SEMI_MATCH_FIELDS)
  const rightSemiConfigs = buildConfiguredMatches(SEMI_LAYOUT.right, SEMI_MATCH_FIELDS)
  const mobileRoundOneConfigs = [...leftRoundOneConfigs, ...rightRoundOneConfigs]
  const mobileRoundTwoConfigs = [...leftRoundTwoConfigs, ...rightRoundTwoConfigs]
  const mobileQuarterConfigs = [...leftQuarterConfigs, ...rightQuarterConfigs]
  const mobileSemiConfigs = [...leftSemiConfigs, ...rightSemiConfigs]

  const desktopColumn = (children, className = '') => <div className={`bracket-v2-column ${className}`}>{children}</div>
  const mobileColumn = (title, date, children, className = '') => (
    <section className={`bracket-v2-mobile-column ${className}`} key={`${title}-${date}`}>
      <div className="round-details primary wc-font wc-secondary">{title}<span className="date wc-quaternary">{date}</span></div>
      <div className="bracket-v2-mobile-column-body">{children}</div>
    </section>
  )

  useEffect(() => {
    const scroller = mobileScrollerRef.current
    if (!scroller) return undefined

    let frameId = null
    const updateFocusIndex = () => {
      frameId = null
      const firstColumn = scroller.querySelector('.bracket-v2-mobile-column')
      if (!firstColumn) return
      const track = scroller.querySelector('.bracket-v2-mobile-track')
      const gap = track ? parseFloat(window.getComputedStyle(track).columnGap || window.getComputedStyle(track).gap || '0') : 0
      const columnWidth = firstColumn.getBoundingClientRect().width + gap
      if (!columnWidth) return
      const nextIndex = Math.min(4, Math.max(0, Math.round(scroller.scrollLeft / columnWidth)))
      setMobileFocusIndex(current => (current === nextIndex ? current : nextIndex))
    }

    const handleScroll = () => {
      if (frameId !== null) return
      frameId = window.requestAnimationFrame(updateFocusIndex)
    }

    updateFocusIndex()
    scroller.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('resize', updateFocusIndex)
    return () => {
      scroller.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', updateFocusIndex)
      if (frameId !== null) window.cancelAnimationFrame(frameId)
    }
  }, [])

  return (
    <div className={removeWhiteSpace(WC_NAME)}>
      <div className={`background-image bracket-form-background ${viewMode === 'group' ? 'bracket-form-background-phase-one' : 'bracket-form-background-phase-three'}`}/>
      <div className="bracket-v2-form">
        <header className="bracket-header">
          {showBackButton && <BackButton onClick={backButtonOnClick} text={backButtonText} />}
          <div className="header-text bracket-v2-title">
            <div className="bracket-v2-header-top">
              <p className="bracket-name wc-quaternary">
                {bracket.name}
                {canEdit && <Tooltip placement="bottom" title="Click to edit bracket.">
                  <span className="edit-bracket-icon" onClick={() => navigate(`/edit/${bracket.id}`)}>
                    <FontAwesomeIcon className="wc-quaternary" icon={faPenToSquare} />
                  </span>
                </Tooltip>}
              </p>
              <p className="bracket-v2-score wc-quaternary">
                <span>{bracket.score ?? 0}</span>
                <span className="bracket-v2-possible-points">{` (${bracket.possible_points ?? 0})`}</span>
              </p>
              {!bracket.second_chance && (
                <div className="bracket-v2-view-toggle">
                  <Button
                    onClick={() => setViewMode(viewMode === 'knockout' ? 'group' : 'knockout')}
                    disabled={viewMode === 'knockout' && !hasPhaseOneData}
                  >
                    {viewMode === 'knockout' ? 'View group stage' : 'View knockout stage'}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </header>

        {viewMode === 'group' && (
          <BracketV2PhaseOneView picks={picks} rankings={rankings} scorecard={scorecard} />
        )}

        {viewMode === 'knockout' && <>
        <div className={`bracket-v2-mobile mobile-focus-${mobileFocusIndex}`} ref={mobileScrollerRef}>
          <div className="bracket-v2-mobile-track">
            {mobileColumn('Thirty-Two', formatRoundDate(scorecard.dates.round_1), renderConfiguredRound({ bracket: picks, configs: mobileRoundOneConfigs, getReadonlyOutcome, rankings }), 'round-one-column')}
            {mobileColumn('Sixteen', formatRoundDate(scorecard.dates.round_2), renderConfiguredRound({ bracket: picks, configs: mobileRoundTwoConfigs, getReadonlyOutcome, rankings }), 'round-two-column')}
            {mobileColumn('Quarters', formatRoundDate(scorecard.dates.quarter), renderConfiguredRound({ bracket: picks, configs: mobileQuarterConfigs, getReadonlyOutcome, rankings }), 'quarter-column')}
            {mobileColumn('Semis', formatRoundDate(scorecard.dates.semi), renderConfiguredRound({ bracket: picks, configs: mobileSemiConfigs, getReadonlyOutcome, rankings }), 'semi-column')}
            {mobileColumn('Final', formatRoundDate(scorecard.dates.final), [
              <div className="winner-flag-container bracket-v2-winner-flag" key="mobile-winner-flag">
                <img className="winner-flag" src={winnerFlag} alt={picks.pick_winner ? `Winner flag for ${picks.pick_winner}` : 'Winner flag placeholder'} />
              </div>,
              renderSinglePick({ field: 'pick_winner', getReadonlyOutcome, label: 'Winner', picks, rankings }),
              <KnockoutMatchupV2 key="final-match" bracket={picks} field="pick_winner" getReadonlyOutcome={getReadonlyOutcome} label="Match 104" onChange={() => null} championship readonly showSelection={false} options={[
                picks.pick_f1 ? buildTeamOption(picks.pick_f1, rankings, 'pick_f1') : null,
                picks.pick_f2 ? buildTeamOption(picks.pick_f2, rankings, 'pick_f2') : null,
              ]} />,
              <div className="bracket-v2-third-heading round-details primary wc-secondary" key="mobile-third-heading">
                Third
                <span className="date wc-quaternary">{formatRoundDate(scorecard.dates.third)}</span>
              </div>,
              <KnockoutMatchupV2 key="third-match" bracket={picks} field="pick_third" getReadonlyOutcome={getReadonlyOutcome} label="Match 103" onChange={() => null} championship readonly showSelection={false} options={thirdPlaceOptions()} />,
              renderSinglePick({ field: 'pick_third', getReadonlyOutcome, label: 'Third Place', picks, rankings }),
            ], 'bracket-v2-mobile-center')}
          </div>
        </div>

        <div className="bracket-v2-desktop">
          <div className="bracket-v2-header-row">
            <div className="round-details primary wc-font wc-secondary">Thirty-Two<span className="date wc-quaternary">{formatRoundDate(scorecard.dates.round_1)}</span></div>
            <div className="round-details primary wc-font wc-secondary">Sixteen<span className="date wc-quaternary">{formatRoundDate(scorecard.dates.round_2)}</span></div>
            <div className="round-details primary wc-font wc-secondary">Quarters<span className="date wc-quaternary">{formatRoundDate(scorecard.dates.quarter)}</span></div>
            <div className="round-details primary wc-font wc-secondary">Semis<span className="date wc-quaternary">{formatRoundDate(scorecard.dates.semi)}</span></div>
            <div className="round-details primary wc-font wc-secondary">Final<span className="date wc-quaternary">{formatRoundDate(scorecard.dates.final)}</span></div>
            <div className="round-details primary wc-font wc-secondary">Semis<span className="date wc-quaternary">{formatRoundDate(scorecard.dates.semi)}</span></div>
            <div className="round-details primary wc-font wc-secondary">Quarters<span className="date wc-quaternary">{formatRoundDate(scorecard.dates.quarter)}</span></div>
            <div className="round-details primary wc-font wc-secondary">Sixteen<span className="date wc-quaternary">{formatRoundDate(scorecard.dates.round_2)}</span></div>
            <div className="round-details primary wc-font wc-secondary">Thirty-Two<span className="date wc-quaternary">{formatRoundDate(scorecard.dates.round_1)}</span></div>
          </div>
          <div className="bracket-v2-grid">
            {desktopColumn(renderConfiguredRound({ bracket: picks, configs: leftRoundOneConfigs, getReadonlyOutcome, rankings }), 'side-left round-one-column')}
            {desktopColumn(renderConfiguredRound({ bracket: picks, configs: leftRoundTwoConfigs, getReadonlyOutcome, rankings }), 'side-left round-two-column')}
            {desktopColumn(renderConfiguredRound({ bracket: picks, configs: leftQuarterConfigs, getReadonlyOutcome, rankings }), 'side-left quarter-column')}
            {desktopColumn(renderConfiguredRound({ bracket: picks, configs: leftSemiConfigs, getReadonlyOutcome, rankings }), 'side-left semi-column')}

            <div className="bracket-v2-column bracket-v2-center">
              <div className="winner-flag-container bracket-v2-winner-flag">
                <img className="winner-flag" src={winnerFlag} alt={picks.pick_winner ? `Winner flag for ${picks.pick_winner}` : 'Winner flag placeholder'} />
              </div>
              {renderSinglePick({ field: 'pick_winner', getReadonlyOutcome, label: 'Winner', picks, rankings })}
              <KnockoutMatchupV2 bracket={picks} field="pick_winner" getReadonlyOutcome={getReadonlyOutcome} label="Match 104" onChange={() => null} championship readonly showSelection={false} options={[
                picks.pick_f1 ? buildTeamOption(picks.pick_f1, rankings, 'pick_f1') : null,
                picks.pick_f2 ? buildTeamOption(picks.pick_f2, rankings, 'pick_f2') : null,
              ]} />
              <div className="bracket-v2-third-heading round-details primary wc-secondary">
                Third
                <span className="date wc-quaternary">{formatRoundDate(scorecard.dates.third)}</span>
              </div>
              <KnockoutMatchupV2 bracket={picks} field="pick_third" getReadonlyOutcome={getReadonlyOutcome} label="Match 103" onChange={() => null} championship readonly showSelection={false} options={thirdPlaceOptions()} />
              {renderSinglePick({ field: 'pick_third', getReadonlyOutcome, label: 'Third Place', picks, rankings })}
            </div>

            {desktopColumn(renderConfiguredRound({ bracket: picks, configs: rightSemiConfigs, getReadonlyOutcome, rankings }), 'side-right semi-column')}
            {desktopColumn(renderConfiguredRound({ bracket: picks, configs: rightQuarterConfigs, getReadonlyOutcome, rankings }), 'side-right quarter-column')}
            {desktopColumn(renderConfiguredRound({ bracket: picks, configs: rightRoundTwoConfigs, getReadonlyOutcome, rankings }), 'side-right round-two-column')}
            {desktopColumn(renderConfiguredRound({ bracket: picks, configs: rightRoundOneConfigs, getReadonlyOutcome, rankings }), 'side-right round-one-column')}
          </div>
        </div>
        </>}
      </div>
    </div>
  )
}
